import React from 'react';

type BackgroundVariant = 'ocean' | 'sky' | 'space';

interface AnimatedBackgroundProps {
  variant?: BackgroundVariant;
}

const AnimatedBackground: React.FC<AnimatedBackgroundProps> = ({ variant = 'ocean' }) => {
  // Ocean theme - fish, bubbles, seaweed (5x faster)
  const oceanElements = [
    { emoji: '\u{1F41F}', size: 30, speed: 9 },   // Fish
    { emoji: '\u{1F420}', size: 35, speed: 10 },  // Tropical fish
    { emoji: '\u{1F419}', size: 40, speed: 12 },  // Octopus
    { emoji: '\u{1F41A}', size: 25, speed: 11 },  // Shell
    { emoji: '\u{1F990}', size: 30, speed: 12 },  // Shrimp
    { emoji: '\u{1F980}', size: 35, speed: 13 },  // Crab
    { emoji: '\u{1FAB8}', size: 28, speed: 10 },  // Coral
    { emoji: '\u{1F9AA}', size: 22, speed: 10 },  // Oyster
  ];

  // Sky theme - clouds, birds, butterflies, rainbows (5x faster)
  const skyElements = [
    { emoji: '\u2601\uFE0F', size: 50, speed: 12 },   // Cloud
    { emoji: '\u{1F426}', size: 28, speed: 9 },       // Bird
    { emoji: '\u{1F98B}', size: 32, speed: 10 },      // Butterfly
    { emoji: '\u{1F308}', size: 45, speed: 16 },      // Rainbow
    { emoji: '\u2B50', size: 20, speed: 8 },          // Star
    { emoji: '\u{1F338}', size: 25, speed: 11 },      // Cherry blossom
    { emoji: '\u{1F388}', size: 35, speed: 10 },      // Balloon
    { emoji: '\u{1F31E}', size: 40, speed: 18 },      // Sun
  ];

  // Space theme - stars, planets, rockets (5x faster)
  const spaceElements = [
    { emoji: '\u2B50', size: 20, speed: 8 },          // Star
    { emoji: '\u{1F31F}', size: 25, speed: 9 },       // Glowing star
    { emoji: '\u{1FA90}', size: 30, speed: 11 },      // Ringed planet
    { emoji: '\u{1F680}', size: 35, speed: 10 },      // Rocket
    { emoji: '\u{1F6F8}', size: 32, speed: 12 },      // UFO
    { emoji: '\u2604\uFE0F', size: 28, speed: 8 },    // Comet
    { emoji: '\u{1F31D}', size: 40, speed: 14 },      // Moon
    { emoji: '\u{1F47D}', size: 30, speed: 10 },      // Alien
  ];

  const elements = variant === 'ocean' ? oceanElements : variant === 'sky' ? skyElements : spaceElements;

  // Generate random positions for multiple instances of each element
  const allElements = React.useMemo(() => {
    const items: Array<{
      emoji: string;
      size: number;
      speed: number;
      left: number;
      delay: number;
      top: number;
    }> = [];

    elements.forEach((el) => {
      // Create 2-3 instances of each element
      const count = Math.floor(Math.random() * 2) + 2;
      for (let j = 0; j < count; j++) {
        items.push({
          ...el,
          left: Math.random() * 100,
          delay: Math.random() * el.speed,
          top: Math.random() * 100,
        });
      }
    });

    return items;
  }, [elements]);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {/* Bubbles for ocean theme */}
      {variant === 'ocean' && (
        <>
          {[...Array(15)].map((_, i) => (
            <div
              key={`bubble-${i}`}
              className="absolute rounded-full bg-white/20 border border-white/30"
              style={{
                width: Math.random() * 20 + 10,
                height: Math.random() * 20 + 10,
                left: `${Math.random() * 100}%`,
                bottom: '-20px',
                animation: `float-up ${1.5 + Math.random() * 2}s linear infinite`,
                animationDelay: `${Math.random() * 1.5}s`,
              }}
            />
          ))}
        </>
      )}

      {/* Floating elements */}
      {allElements.map((el, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            fontSize: el.size,
            left: `${el.left}%`,
            top: `${el.top}%`,
            animation: `float-around ${el.speed}s ease-in-out infinite`,
            animationDelay: `${el.delay}s`,
            opacity: 0.6,
          }}
        >
          {el.emoji}
        </div>
      ))}

      {/* CSS animations */}
      <style>{`
        @keyframes float-up {
          0% {
            transform: translateY(0) translateX(0);
            opacity: 0.6;
          }
          50% {
            transform: translateY(-50vh) translateX(20px);
            opacity: 0.4;
          }
          100% {
            transform: translateY(-100vh) translateX(-10px);
            opacity: 0;
          }
        }

        @keyframes float-around {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg);
          }
          25% {
            transform: translate(15px, -20px) rotate(5deg);
          }
          50% {
            transform: translate(-10px, -10px) rotate(-5deg);
          }
          75% {
            transform: translate(20px, 10px) rotate(3deg);
          }
        }
      `}</style>
    </div>
  );
};

export default AnimatedBackground;
